/**
 * Opens a short-lived mic stream and watches for real input (not silence / OS mute).
 * Separate from mic-recorder-to-mp3 so we can reject muted/silent takes before upload.
 */

export async function startMicrophoneInputMonitor(options = {}) {
  const intervalMs = options.intervalMs ?? 120;
  /** Min deviation from 128 in getByteTimeDomainData (0–255) to count as sound */
  const sampleThreshold = options.sampleThreshold ?? 14;

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const [track] = stream.getAudioTracks();
  if (!track) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error("No audio track from microphone");
  }

  let trackWasMuted = false;
  const onMute = () => {
    trackWasMuted = true;
  };
  track.addEventListener("mute", onMute);

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioContextClass();
  await ctx.resume();

  const levelCheckActive = ctx.state === "running";
  let heardSignal = false;

  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const data = new Uint8Array(analyser.fftSize);

  const tick = () => {
    if (track.muted) {
      trackWasMuted = true;
    }
    if (!levelCheckActive || ctx.state !== "running") {
      return;
    }
    analyser.getByteTimeDomainData(data);
    for (let i = 0; i < data.length; i++) {
      if (Math.abs(data[i] - 128) > sampleThreshold) {
        heardSignal = true;
        break;
      }
    }
  };

  const id = setInterval(tick, intervalMs);

  return {
    getSummary: () => ({
      heardSignal,
      trackWasMuted,
      levelCheckActive,
    }),
    dispose: async () => {
      clearInterval(id);
      track.removeEventListener("mute", onMute);
      try {
        source.disconnect();
      } catch (_) {}
      stream.getTracks().forEach((t) => t.stop());
      try {
        await ctx.close();
      } catch (_) {}
    },
  };
}

/**
 * @param {{ heardSignal: boolean, trackWasMuted: boolean, levelCheckActive: boolean }} summary
 * @returns {{ ok: boolean, reason: 'mute' | 'silent' | null }}
 */
export function evaluateMicrophoneSummary(summary) {
  if (summary.trackWasMuted) {
    return { ok: false, reason: "mute" };
  }
  if (summary.levelCheckActive && !summary.heardSignal) {
    return { ok: false, reason: "silent" };
  }
  return { ok: true, reason: null };
}
